import { Link, useParams } from "react-router-dom";
import { PaymentCard } from "../application-card-details";
import { TabContainer, TabPanelProp } from "@/components/Tabs";
import { useState } from "react";
import { SquareArrowLeft } from "lucide-react";
import { useGetInvestmentApprovalStatus, useGetInvestmentDetails, useResubmitInvestment } from "@/features/applications/repositories/investment-repository";
import { useGetPaymentByApplicationId } from "@/features/applications/repositories/licences-repository";
import { Investment } from "@/types";

import InvestmentDetailsCard from "./investment-details-card";
import ConstructionDetails from "./application-investment-construction-details";
import WorkforceDetails from "./application-investment-workforce-details";
import ApplicationDetailsCard from "../application-card-details";
import PaymentDetailsSlideOver from "../payments/payment-details-slide-over";
import InvestmentPaymentModal from "../../form/investmenr-payment-modal";
import Button from "@/components/buttons/Button";
import ApprovalProgressPanel from "../approval-progress-panel";
import ReturnedForCorrectionNotice from "../returned-for-correction-notice";
import InvestmentInspectionPanel from "./investment-inspection-panel";
import { investmentInspectionFee } from "../../../constants/fees";
import useAuth from "@/providers/auth/useAuth";



/**
 * Two recorded headcounts added together — undefined when the application
 * recorded neither, so the row is dropped rather than shown as a real zero.
 */
function sumCounts(first?: number, second?: number): number | undefined {
    if (first === undefined && second === undefined) return undefined;
    return (first ?? 0) + (second ?? 0);
}

/**
 * Reads `ApplicationResponseDto` (GET /investment/{applicationId}). That payload
 * carries no contact details and no fee breakdown, so contact rows come from the
 * applicant's own account and the inspection fee from constants/fees.ts.
 */
export default function ApplicationInvestmentDetails() {
    const [show, setShow] = useState<boolean>(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    const { applicationId } = useParams()
    const { user } = useAuth()
    const { investiment, isFetching, isLoading } = useGetInvestmentDetails(applicationId!)
    const { payment, isLoading: isPaymentLoading, refetch: refetchPayment } = useGetPaymentByApplicationId(applicationId!)
    const {
        approval_status,
        isLoading: isApprovalLoading,
        isError: isApprovalError,
    } = useGetInvestmentApprovalStatus(applicationId!)
    const resubmit = useResubmitInvestment(applicationId!)

    const tabs: TabPanelProp[] = [
        { name: "Details", identifier: "details" },
        { name: "Construction", identifier: "construction" },
        { name: "Inspection", identifier: "inspection" }
    ]

    const investmentDetails = (investiment?.results ?? investiment) as Investment | undefined
    const approval = approval_status?.results ?? undefined
    const projectActivities = (investmentDetails?.businessDetails?.projectActivities ?? [])
        .filter(Boolean)
        .join(", ")

    // Neither `BusinessDetails` nor the response DTO carries contact details, so
    // they are taken from the account that owns the application.
    const contactName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || undefined
    const contactEmail = user?.business?.businessEmail || user?.email || undefined
    const contactPhone = user?.business?.businessPhone || user?.phoneNumber || undefined
    const numberOfRooms = investmentDetails?.businessDetails?.numberOfRooms || undefined
    const construction = investmentDetails?.construction
    const workforce = investmentDetails?.workforce

    if (isLoading || isFetching || isPaymentLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
        )
    }

    // The API exposes no fee calculation for an investment application; the
    // inspection fee is a published, zone-dependent constant (constants/fees.ts).
    // Once a bill exists, the amount actually billed wins.
    const inspectionFee = investmentInspectionFee(investmentDetails?.businessDetails?.zone);
    const totalFees = payment?.amount ?? inspectionFee;

    return (
        <div>
            <header className="bg-white px-4 py-2 border-b border-t border-secondary-100 flex justify-between items-center gap-4">
                <Link to="/applications/investments">
                    <Button
                        intent="text"
                        leftIcon={<SquareArrowLeft className="size-5" />}
                        className="text-base text-secondary font-light"
                    >
                        Application Details
                    </Button>
                </Link>
            </header>
            <div className="mt-3 px-4">

                <ReturnedForCorrectionNotice
                    approval={approval}
                    fallbackReason={investmentDetails?.comment}
                    onResubmit={() => resubmit.mutate()}
                    isResubmitting={resubmit.isPending}
                    error={resubmit.error}
                />

                <InvestmentPaymentModal
                    isOpen={showPaymentModal}
                    onClose={() => setShowPaymentModal(false)}
                    onSubmit={() => setShowPaymentModal(false)}
                    inspectionFee={inspectionFee.toString()}
                    totalFees={totalFees?.toString()}
                    // Links the bill to this application so the fee, control number and payment
                    // status resolve back onto it.
                    applicationId={investmentDetails?.applicationId}
                    billDesc={`Investment Inspection Fee — ${investmentDetails?.businessDetails?.projectName ?? ''}`}
                    custName={contactName ?? investmentDetails?.businessDetails?.projectName}
                    custEmail={contactEmail}
                    custAccnt={investmentDetails?.applicationId}
                    custTin={investmentDetails?.tin}
                    custIdTyp={investmentDetails?.tin ? '4' : undefined}
                    custId={investmentDetails?.tin ?? investmentDetails?.applicationId}
                    defaultPhoneNumber={contactPhone}
                />

                <PaymentDetailsSlideOver
                    setShow={() => setShow(!show)}
                    show={show}
                    controlNumber={payment?.controlNumber}
                    onReused={() => refetchPayment()}
                    paymentData={payment ? {
                        customerName: contactName ?? investmentDetails?.businessDetails?.projectName,
                        projectName: investmentDetails?.businessDetails?.projectName,
                        referenceNumber: payment?.referenceNumber,
                        paymentStatus: payment?.status ?? payment?.paymentStatus,
                        amountPaid: payment?.amount,
                        paymentMethod: payment?.paymentType ?? payment?.paymentMethod,
                        phoneNumber: contactPhone ?? payment?.phoneNumber,
                        referenceId: payment?.referenceNumber ?? payment?.referenceId,
                        date: payment?.paymentDate ?? payment?.createdAt,
                        generatedDate: payment?.billGenDt,
                        expiryDate: payment?.billExprDt,
                        billedItem: payment?.billedItem,
                        amountInWords: payment?.amountInWords,
                        preparedBy: payment?.preparedBy,
                    } : undefined}
                />

                <div className="sm:flex sm:justify-between w-full gap-5">
                    <ApplicationDetailsCard
                        status={investmentDetails?.applicationStatus ?? "Unknown"}
                        paymentStatus={payment?.status ?? payment?.paymentStatus}
                        title={investmentDetails?.businessDetails?.projectName ?? "Investment application"}
                        applicationId={investmentDetails?.applicationId ?? "—"}
                        submissionDate={investmentDetails?.businessDetails?.dateIssued}
                        applicationStatus={investmentDetails?.applicationStatus ?? "Unknown"}
                        projectName={investmentDetails?.businessDetails?.projectName ?? "—"}
                        projectPOBox={investmentDetails?.businessDetails?.poBox}
                        emailAddress={contactEmail}
                        phoneNumber={contactPhone}
                        projectLocation={investmentDetails?.businessDetails?.street}
                        projectActivities={projectActivities || undefined}
                        numberOfRooms={numberOfRooms}
                        targetMarketDescription={investmentDetails?.businessDetails?.describeTargetAudience}
                    />
                    <div className="flex flex-col w-full gap-5">
                        <TabContainer panels={tabs} panelClassName="mt-3">
                            <div>
                                <InvestmentDetailsCard
                                    titleHeader="Investment Details"
                                    investMentAmount={investmentDetails?.capitalInvestment?.estimatedInvestmentAmount}
                                    personalContribution={investmentDetails?.capitalInvestment?.personalContribution}
                                    loanAmount={investmentDetails?.capitalInvestment?.loanAmount}
                                    institutionProvidingLoanName={investmentDetails?.capitalInvestment?.institutionProvidingLoan}
                                    bankName={investmentDetails?.capitalInvestment?.bankName}
                                    bankAddress={investmentDetails?.capitalInvestment?.bankAddress}
                                    shareholders={investmentDetails?.capitalInvestment?.shareholders ?? []}
                                />

                                <PaymentCard
                                    reference={payment?.referenceNumber ?? payment?.controlNumber}
                                    inspectionFee={inspectionFee.toString()}
                                    totalFees={totalFees?.toString()}
                                    paymentDate={payment?.paymentDate ?? payment?.createdAt}
                                    onGenerateControlNumber={() => setShowPaymentModal(true)}
                                    viewDetailsHandler={() => setShow(true)}
                                />

                                <ApprovalProgressPanel
                                    approval={approval}
                                    isLoading={isApprovalLoading}
                                    isError={isApprovalError}
                                />
                            </div>

                            <div>
                                <ConstructionDetails
                                    sizeOfLand={construction?.landSizeInHectares}
                                    willUseExistingBuildings={construction
                                        ? (construction.existingBuildingsUsed ? "Yes" : "No")
                                        : undefined}
                                    constructionCompletionTime={construction?.constructionCompletionTime}
                                    projectLifespan={construction?.projectLifespanYears}
                                    environmentalManagementPlans={construction?.environmentalManagementPlans}
                                />

                                <WorkforceDetails
                                    localEmployees={sumCounts(workforce?.localPermanentEmployees, workforce?.localTemporaryEmployees)}
                                    foreignEmployees={sumCounts(workforce?.foreignPermanentEmployees, workforce?.foreignTemporaryEmployees)}
                                    permanentEmployees={sumCounts(workforce?.localPermanentEmployees, workforce?.foreignPermanentEmployees)}
                                    temporaryEmployees={sumCounts(workforce?.localTemporaryEmployees, workforce?.foreignTemporaryEmployees)}
                                />
                            </div>

                            <div className="space-y-5">
                                <InvestmentInspectionPanel
                                    inspectionPhase={approval?.inspectionPhase}
                                    isLoading={isApprovalLoading}
                                    isError={isApprovalError}
                                />
                            </div>
                        </TabContainer>
                    </div>
                </div>
            </div>
        </div>
    )
}
